import React from 'react';
import { useTitle } from '../hooks/useTitle';
import { RoiCalculator } from '../components/RoiCalculator';
import { Calculator, Target, Brain, TrendingUp, CheckCircle2 } from 'lucide-react';
import { useEffect } from 'react';

export function RoiCalculatorPage() {
  const { setTitle } = useTitle();
  
  useEffect(() => {
    setTitle('Calculateur de ROI - Novster AI');
  }, [setTitle]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-6 animate-slideUp">
            Calculez votre retour sur investissement IA
          </h1>
          <p className="text-xl text-center text-gray-600 max-w-3xl mx-auto mb-12 animate-slideUp">
            Estimez précisément les gains potentiels de l'IA pour votre entreprise grâce à notre calculateur basé sur des données réelles
          </p>
        </div>
      </div>

      {/* Methodology Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Notre méthodologie de calcul</h2>
          <p className="text-xl text-center text-gray-600 max-w-3xl mx-auto mb-16">
            Notre calculateur combine trois sources de gains pour une estimation précise de votre ROI sur 5 ans
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all">
              <Target className="w-12 h-12 text-orange-500 mb-6" />
              <h3 className="text-xl font-bold mb-4">Gains de rentabilité</h3>
              <p className="text-gray-600 mb-6">
                Calculés en appliquant un multiplicateur sectoriel à votre CA actuel :
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>E-commerce : 10% d'optimisation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Services B2B : 12% d'optimisation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Distribution : 8% d'optimisation</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all">
              <Brain className="w-12 h-12 text-orange-500 mb-6" />
              <h3 className="text-xl font-bold mb-4">Gains de productivité</h3>
              <p className="text-gray-600 mb-6">
                Calculés selon la formule : Heures économisées × Coût horaire × 52 semaines
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Automatisation des tâches répétitives</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Gain de temps sur les processus</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Valorisation monétaire du temps</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all">
              <TrendingUp className="w-12 h-12 text-orange-500 mb-6" />
              <h3 className="text-xl font-bold mb-4">Nouveaux revenus</h3>
              <p className="text-gray-600 mb-6">
                Calculés avec un taux de croissance composé selon votre secteur :
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>E-commerce : +5% par an</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Services B2B : +10% par an</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Distribution : +4% par an</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 bg-orange-50 rounded-xl p-8 max-w-4xl mx-auto">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Calculator className="w-6 h-6 text-orange-500" />
              Formule de calcul détaillée
            </h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                <strong>1. Gain annuel de base (G₁) :</strong><br />
                G₁ = (Heures économisées × Coût horaire × 52) + (CA × Multiplicateur sectoriel)
              </p>
              <p className="text-gray-700">
                <strong>2. Gains des années suivantes (Gₙ) :</strong><br />
                Gₙ = G₁ × (1 + Taux de croissance sectoriel)^(n-1)
              </p>
              <p className="text-gray-700">
                <strong>3. Coût total (C) :</strong><br />
                C = Investissement initial + (Coût annuel × 5 ans)
              </p>
              <p className="text-gray-700">
                <strong>4. ROI global :</strong><br />
                ROI = ((Somme des gains sur 5 ans - Coût total) / Coût total) × 100
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Questions fréquentes sur le ROI</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold mb-4">Comment les multiplicateurs sont-ils calculés ?</h3>
              <p className="text-gray-600">
                Nos multiplicateurs sont basés sur les données réelles collectées auprès de plus de 100 entreprises
                ayant implémenté nos solutions IA. Ils sont ajustés en fonction du secteur d'activité et de la
                taille de l'entreprise pour refléter les gains réellement observables.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold mb-4">En combien de temps ces résultats sont-ils atteignables ?</h3>
              <p className="text-gray-600">
                Les premiers résultats sont visibles dès 30 jours, avec une montée en puissance progressive sur 90 jours.
                Les gains de rentabilité sont généralement les premiers à se matérialiser, suivis des gains de productivité.
                Les nouveaux revenus se développent typiquement sur 6 à 12 mois.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold mb-4">Ces estimations sont-elles garanties ?</h3>
              <p className="text-gray-600">
                Les résultats présentés sont des moyennes basées sur notre expérience. Nous garantissons un ROI minimum
                de 3x sur l'investissement initial. Les résultats réels peuvent varier en fonction de nombreux facteurs
                comme votre niveau de maturité digitale, votre capacité d'exécution et votre engagement dans la transformation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 w-full">
        <div className="w-full max-w-[90vw] mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Calculez votre ROI</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Estimez vos gains potentiels en fonction de votre secteur d'activité et de votre chiffre d'affaires
            </p>
          </div>
          <RoiCalculator />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">Prêt à concrétiser ces gains ?</h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto">
            Réservez votre diagnostic gratuit et découvrons ensemble comment maximiser votre ROI avec l'IA
          </p>
          <a
            href="https://tidycal.com/quentin.tousart/novster-ai"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-orange-500 px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-gray-100 transform hover:scale-105 transition-all"
          >
            Réserver mon diagnostic
            <Calculator className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}